import ApplicationController from '../application_controller'

export default class extends ApplicationController {

	static targets = [
		'activityItem',
		'participantItem',
		'detailsSection',
		'meetingTopicsSection',
		'sessionIndicatorList',
	]

	static values = {
		activitySections: { type: Array, default: [] }
	}

	/* This can't be trusted because ChoicesController initialization has side-effects
	 * that can leave an invalid state for the select input.
	activityItemTargetConnected(target) {
		this.currentActivitiesIndex ||= 0
		this.currentActivities ||= []

		// `sessionRelationshipsActivityIndex` is used for associating currentActivities elements
		// back to activityItemTargets.
		target.dataset.sessionRelationshipsActivityIndex = this.currentActivitiesIndex
		this.currentActivitiesIndex += 1

		const activityModel = this.bindActivityModelFromActivityItemElement(target)
		this.currentActivities.push(activityModel)
	}
	*/

	// this is used to react to choices-initialized events. ChoicesController has side-effects
	// that make the selectTarget state invalid and incorrect.
	connectActivityItemTarget({ detail: { selectElement }}) {
		const target = this.activityItemTargets.find(activityItem => activityItem.contains(selectElement))
		if (!target) { return }

		this.currentActivitiesIndex ||= 0
		this.currentActivities ||= []

		// `sessionRelationshipsActivityIndex` is used for associating currentActivities elements
		// back to activityItemTargets.
		target.dataset.sessionRelationshipsActivityIndex = this.currentActivitiesIndex
		this.currentActivitiesIndex += 1

		const activityModel = this.bindActivityModelFromActivityItemElement(target)
		this.currentActivities.push(activityModel)
		this.updateActivitySections()
	}

	bindActivityModelFromActivityItemElement(activityItemElement) {
		const activityModel = {}
		activityModel.activity_index = activityItemElement.dataset.sessionRelationshipsActivityIndex

		activityModel.activity_id    = activityItemElement
			.querySelector('select[name="session[session_activities_attributes][][activity_id]"]')
			.selectedOptions[0]
			?.value

		activityModel.duration       = activityItemElement
			.querySelector('input[name="session[session_activities_attributes][][duration]"]')
			.value

		return activityModel
	}

	activityItemTargetDisconnected(target) {
		this.currentActivities = this.currentActivities
			.filter(activity => activity.activity_index != parseInt(target.dataset.sessionRelationshipsActivityIndex))

		// This is hack to enforce ChoicesController.disconnect gets called.
		// disconnect fails to get called due to a bug in reference counting for outlets.
		// more information: https://github.com/hotwired/stimulus/issues/763
		const choicesElement = target.querySelector('[data-controller="choices"]')
		const choicesController = this.application.getControllerForElementAndIdentifier(choicesElement, 'choices')
		choicesController?.disconnect()

		this.updateAllParticipantItems()
	}

	activityItemChanged(event) {
		const changedActivityItem = event.currentTarget
			.closest('div[data-app--session-relationships-target="activityItem"]')

		this.currentActivities = this.currentActivities
			.filter(activity => activity.activity_index != parseInt(changedActivityItem.dataset.sessionRelationshipsActivityIndex))

		const activityModel = this.bindActivityModelFromActivityItemElement(changedActivityItem)
		this.currentActivities.push(activityModel)
		this.updateAllParticipantItems()
		this.updateActivitySections()
	}

	updateActivitySections() {
		if (this.activitySectionsValue.length > 0) {
			for (const activity of this.activitySectionsValue) {
				if (this.currentActivities.some((current_activity) => current_activity.activity_id == activity.activity_id )) {
					for (const hideSection of activity.hide_session_sections) {
						this[`${hideSection}Target`].classList.add('d-none')
					}
					for (const showSection of activity.show_session_sections) {
						this[`${showSection}Target`].classList.remove('d-none')
					}
				} else {
					for (const hideSection of activity.hide_session_sections) {
						this[`${hideSection}Target`].classList.remove('d-none')
					}
					for (const showSection of activity.show_session_sections) {
						this[`${showSection}Target`].classList.add('d-none')
					}
				}
			}
		}
	}

	updateAllParticipantItems() {
		for (const participantItem of this.participantItemTargets) {
			const updatedActivityModels = []
			const spaActivityIdInputs = participantItem.querySelectorAll("input[name=\"session[session_participant_activities_attributes][][activity_id]\"]")

			for (const spaActivityIdInput of spaActivityIdInputs) {
				const activityModel = this.currentActivities.find(activityModel => activityModel.activity_id == spaActivityIdInput.value)
			
				const participantItemSpaElement = spaActivityIdInput.closest('.participant-item-spa')

				if (!activityModel) {
					participantItemSpaElement.remove()
				} else {
					this.updateSpaElement(participantItemSpaElement, activityModel)
					updatedActivityModels.push(activityModel)
				}
			}

			if (updatedActivityModels.length < this.currentActivities.length) {
				const participantItemSpasElement = participantItem.querySelector('div.participant-item-spas')

				this.currentActivities
					.filter(activityModel => !updatedActivityModels.includes(activityModel))
					.filter(activityModel => activityModel.activity_id != '')
					.map(activityModel => this.createSpaElement(participantItem.dataset.participantId, activityModel))
					.forEach(spaElement => participantItemSpasElement.append(spaElement))
			}
		}
	}

	updateSpaElement(participantItemSpaElement, activityModel) {
		const inputValueTuples = [
			["input[name=\"session[session_participant_activities_attributes][][activity_id]\"]", activityModel.activity_id],
			["input[name=\"session[session_participant_activities_attributes][][duration]\"]"   , activityModel.duration]
		]

		for (const [inputSelector, newValue] of inputValueTuples) {
			participantItemSpaElement.querySelector(inputSelector).value = newValue
		}
	}

	participantItemTargetConnected(target) {
		if (target.dataset.hasActivities != 'true') {

			let participantItemSpasElement = target.querySelector('div.participant-item-spas')
			if (!participantItemSpasElement) { 
				participantItemSpasElement = document.createElement('div')
				participantItemSpasElement.classList.add('participant-item-spas')
			}

			for (const activityModel of this.currentActivities.filter(activity => activity.activity_id != '')) {
				const spaElement = this.createSpaElement(target.dataset.participantId, activityModel)
				participantItemSpasElement.append(spaElement)
			}

			target.prepend(participantItemSpasElement)
			target.dataset.hasActivities = 'true'
		}
	}

	createSpaElement(participantId, activityModel) {
		const spaElement = document.createElement('div')
		spaElement.classList.add('participant-item-spa')
		spaElement.innerHTML += `
<input type="hidden" data-app--session-participant-item-target="spaInput" name="session[session_participant_activities_attributes][][_destroy]" value="0">
<input type="hidden" data-app--session-participant-item-target="spaInput" name="session[session_participant_activities_attributes][][id]" value="">
<input type="hidden" data-app--session-participant-item-target="spaInput" name="session[session_participant_activities_attributes][][participant_id]" value="${participantId}">
<input type="hidden" data-app--session-participant-item-target="spaInput" name="session[session_participant_activities_attributes][][activity_id]" value="${activityModel.activity_id}">
<input type="hidden" data-app--session-participant-item-target="spaInput" name="session[session_participant_activities_attributes][][duration]" value="${activityModel.duration}">
`
		return spaElement
	}

}
